/* Buyer-side location context and checkout guard.
 * This is a storefront UX guard only. The Cart and Checkout Validation
 * Function cannot validate this token, service area, live stock, or fee.
 */
(function (root) {
  'use strict';

  var LOCATION_KEY = 'samaanbox:buyer-location:v1';
  var INVALIDATION_KEY = 'samaanbox:buyer-location-context-invalidated:v1';
  var ATTR_CONTEXT = '_samaanbox_location_context';
  var ATTR_CART = '_samaanbox_context_cart_token';
  var ATTR_FINGERPRINT = '_samaanbox_context_fingerprint';
  var ATTR_EXPIRES = '_samaanbox_context_expires_at';
  var ATTRIBUTES = [ATTR_CONTEXT, ATTR_CART, ATTR_FINGERPRINT, ATTR_EXPIRES];
  var MAX_LINES = 50;
  var MAX_QUANTITY = 1000;
  var generation = 0;
  var attributeWriteQueue = Promise.resolve();
  var expiryTimer = null;
  var preparing = false;

  function getConfig() {
    return root.SamaanBoxCartContextConfig || {};
  }

  function getStorage() {
    try { return root.sessionStorage; } catch (error) { return null; }
  }

  function validPoint(point) {
    if (!point || typeof point !== 'object') return null;
    var latitude = Number(point.latitude);
    var longitude = Number(point.longitude);
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) return null;
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) return null;
    return { latitude: latitude, longitude: longitude };
  }

  function pointEquals(left, right) {
    if (!left || !right) return left === right;
    return left.latitude === right.latitude && left.longitude === right.longitude;
  }

  function storePoint(point) {
    var storage = getStorage();
    if (!storage) return;
    try {
      if (point) storage.setItem(LOCATION_KEY, JSON.stringify(point));
      else storage.removeItem(LOCATION_KEY);
    } catch (error) { /* session storage is optional */ }
  }

  function rememberPoint(point) {
    var normalized = validPoint(point);
    if (pointEquals(readPoint(), normalized)) return;
    var ownGeneration = ++generation;
    markInvalidated();
    setPreparing(false);
    renderState('pending', false, false);
    storePoint(normalized);
    fetchCart().then(function (cart) {
      if (ownGeneration !== generation || !cart) return false;
      return clearStoredContext(ownGeneration).then(function (cleared) {
        if (ownGeneration !== generation) return false;
        if (cleared) clearInvalidationMarker();
        renderState(cart.items.length ? 'pending' : 'empty', false, false);
        return cleared;
      });
    }).catch(function () { /* invalidation marker keeps checkout closed */ });
  }

  function readPoint() {
    var storage = getStorage();
    if (!storage) return null;
    try { return validPoint(JSON.parse(storage.getItem(LOCATION_KEY) || 'null')); } catch (error) { return null; }
  }

  function hasInvalidationMarker() {
    var storage = getStorage();
    if (!storage) return true;
    try { return storage.getItem(INVALIDATION_KEY) === '1'; } catch (error) { return true; }
  }

  function markInvalidated() {
    var storage = getStorage();
    if (!storage) return;
    try { storage.setItem(INVALIDATION_KEY, '1'); } catch (error) { /* quote stays disabled in memory */ }
  }

  function clearInvalidationMarker() {
    var storage = getStorage();
    if (!storage) return;
    try { storage.removeItem(INVALIDATION_KEY); } catch (error) { /* optional */ }
  }

  function getCartUrl() {
    var config = getConfig();
    return config.cartUrl || ((root.routes && root.routes.cart_url) ? root.routes.cart_url + '.js' : '/cart.js');
  }

  function getUpdateUrl() {
    var config = getConfig();
    return config.updateUrl || ((root.routes && root.routes.cart_update_url) ? root.routes.cart_update_url : '/cart/update.js');
  }

  function endpointFor(container, explicit) {
    if (explicit) return explicit;
    if (container && container.getAttribute('data-context-endpoint')) return container.getAttribute('data-context-endpoint');
    return getConfig().contextEndpoint || '';
  }

  function normalizeCart(cart) {
    if (!cart || typeof cart !== 'object' || !Array.isArray(cart.items) || typeof cart.token !== 'string' || !cart.token) return null;
    var aggregate = Object.create(null);
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      if (!line || typeof line !== 'object') return null;
      var rawId = line.variant_id != null ? line.variant_id : line.id;
      var id = String(rawId == null ? '' : rawId);
      var match = id.match(/^(?:gid:\/\/shopify\/ProductVariant\/)?([1-9][0-9]*)$/);
      var quantity = Number(line.quantity);
      if (!match || !Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) return null;
      var gid = 'gid://shopify/ProductVariant/' + match[1];
      aggregate[gid] = (aggregate[gid] || 0) + quantity;
      if (aggregate[gid] > MAX_QUANTITY) return null;
    }
    var ids = Object.keys(aggregate).sort();
    if (ids.length > MAX_LINES) return null;
    var items = ids.map(function (variantId) { return { variant_id: variantId, quantity: aggregate[variantId] }; });
    var fingerprint = items.map(function (item) { return item.variant_id.split('/').pop() + ':' + item.quantity; }).join('|');
    return { token: cart.token, items: items, fingerprint: fingerprint, attributes: cart.attributes || {} };
  }

  function parseExpiry(value) {
    if (typeof value !== 'string' || !value) return 0;
    var expires = Date.parse(value);
    return Number.isFinite(expires) ? expires : 0;
  }

  function hasStoredContext(cart) {
    var attrs = cart.attributes || {};
    var expiresAt = parseExpiry(attrs[ATTR_EXPIRES]);
    return !!attrs[ATTR_CONTEXT] && attrs[ATTR_CART] === cart.token &&
      attrs[ATTR_FINGERPRINT] === cart.fingerprint && expiresAt > Date.now();
  }

  function fetchCart() {
    return root.fetch(getCartUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response || response.status < 200 || response.status >= 300) throw new Error('cart_unavailable');
        return response.json();
      }).then(normalizeCart);
  }

  function updateAttributesRaw(attributes) {
    return root.fetch(getUpdateUrl(), {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response || response.status < 200 || response.status >= 300) throw new Error('cart_attribute_update_failed');
      return response.json();
    });
  }

  function serializeAttributeWrite(work) {
    var result = attributeWriteQueue.then(work, work);
    attributeWriteQueue = result.then(function () {}, function () {});
    return result;
  }

  function isCurrent(expectedGeneration) {
    return expectedGeneration === generation;
  }

  function emptyAttributes() {
    var cleared = {};
    ATTRIBUTES.forEach(function (key) { cleared[key] = ''; });
    return cleared;
  }

  /* Inspect inside the serialized lane: a caller snapshot may predate a queued attribute write. */
  function clearStoredContext(expectedGeneration) {
    return serializeAttributeWrite(function () {
      if (expectedGeneration != null && !isCurrent(expectedGeneration)) return false;
      return fetchCart().then(function (current) {
        if (expectedGeneration != null && !isCurrent(expectedGeneration)) return false;
        if (!current) return false;
        var attrs = current.attributes || {};
        if (!ATTRIBUTES.some(function (key) { return !!attrs[key]; })) return true;
        return updateAttributesRaw(emptyAttributes()).then(function () {
          if (expectedGeneration != null && !isCurrent(expectedGeneration)) return false;
          return fetchCart().then(function (after) {
            if (expectedGeneration != null && !isCurrent(expectedGeneration)) return false;
            var finalAttrs = after && after.attributes || {};
            return !ATTRIBUTES.some(function (key) { return !!finalAttrs[key]; });
          });
        });
      }).catch(function () {
        /* checkout remains disabled if invalidation cannot be persisted */
        return false;
      });
    });
  }

  function saveStoredContext(attributes, expectedGeneration) {
    return serializeAttributeWrite(function () {
      if (!isCurrent(expectedGeneration)) return { stale: true };
      function compensate() {
        return updateAttributesRaw(emptyAttributes()).then(fetchCart).then(function () {
          return { stale: true };
        }, function () {
          return { stale: true };
        });
      }
      return updateAttributesRaw(attributes).then(function () {
        if (!isCurrent(expectedGeneration)) return compensate();
        return fetchCart().then(function (after) {
          if (!isCurrent(expectedGeneration)) return compensate();
          if (!after || !hasStoredContext(after)) throw new Error('cart_context_readback_failed');
          return { after: after };
        });
      }).catch(function (error) {
        if (!isCurrent(expectedGeneration)) return compensate();
        throw error;
      });
    });
  }

  function containers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-samaanbox-cart-context]'));
  }

  function message(container, kind) {
    return (container && container.getAttribute('data-message-' + kind)) || {
      ready: 'Delivery checked for this cart.', pending: 'Check delivery before checkout.',
      empty: 'Add an item before checking delivery.', mixed: 'Your items come from different sellers.',
      unserviceable: 'This cart cannot be delivered to your selected location.',
      location: 'Choose a location to check delivery.', unavailable: 'We could not confirm delivery for this cart.'
    }[kind] || '';
  }

  function renderState(kind, error, enable, reason) {
    if (typeof root.CustomEvent === 'function') {
      var detail = { state: kind, ready: enable === true };
      if (reason) detail.reason = reason;
      document.dispatchEvent(new root.CustomEvent('samaanbox:cart-context-state', {
        detail: detail
      }));
    }
    containers().forEach(function (container) {
      var status = container.querySelector('[data-samaanbox-context-status]');
      var button = container.querySelector('[data-samaanbox-context-prepare]');
      if (status) {
        status.textContent = message(container, kind);
        status.setAttribute('role', error ? 'alert' : 'status');
      }
      if (button) button.disabled = preparing;
      if (status && button && !status.id) status.id = 'SamaanBoxContextStatus-' + Math.random().toString(36).slice(2);
      if (status && button) button.setAttribute('aria-describedby', status.id);
    });
    document.querySelectorAll('[name="checkout"]').forEach(function (button) {
      button.disabled = !enable;
      button.setAttribute('aria-disabled', enable ? 'false' : 'true');
      var region = button.closest('form, .cart__ctas, .cart-drawer__footer, [data-samaanbox-cart-context]');
      var status = region && region.querySelector('[data-samaanbox-context-status]');
      if (status) button.setAttribute('aria-describedby', status.id);
    });
  }

  function setPreparing(value) {
    preparing = !!value;
    containers().forEach(function (container) {
      var button = container.querySelector('[data-samaanbox-context-prepare]');
      if (button) button.disabled = preparing;
    });
  }

  function scheduleExpiry(expiresAt) {
    if (expiryTimer) root.clearTimeout(expiryTimer);
    var delay = expiresAt - Date.now();
    if (delay <= 0) return;
    expiryTimer = root.setTimeout(function () { refreshAndMaybeReprepare(true); }, Math.min(delay + 10, 2147483647));
  }

  function stateFromFailure(body) {
    var status = body && body.status;
    var reason = body && body.reason;
    if (status === 'mixed_seller' || reason === 'mixed_seller' || reason === 'multi_seller') return 'mixed';
    if (status === 'unserviceable' || reason === 'unserviceable' || reason === 'out_of_service_area' || reason === 'outside_service_area') return 'unserviceable';
    if (status === 'empty_cart' || reason === 'empty_cart') return 'empty';
    return 'unavailable';
  }

  function normalizedFailureReason(body, state) {
    if (state === 'mixed') return 'mixed_seller';
    if (state === 'unserviceable') return 'unserviceable';
    if (state === 'empty') return 'empty_cart';
    if (body && body.status === 'not_configured') return 'not_configured';
    return body && (body.reason || body.status) || 'context_unavailable';
  }

  function acquireLocation(point) {
    var saved = validPoint(point) || readPoint();
    if (saved) return Promise.resolve(saved);
    // Location permission is requested only by the location dialog's clear
    // button. A cart quote action never prompts for or acquires GPS by itself.
    return Promise.reject(new Error('location_required'));
  }

  function prepare(options) {
    options = options || {};
    var container = options.container || containers()[0] || null;
    var endpoint = endpointFor(container, options.endpoint);
    var ownGeneration = ++generation;
    markInvalidated();
    if (!endpoint) {
      setPreparing(false);
      renderState('pending', false, false);
      renderState('unavailable', true, false, 'not_configured');
      return Promise.resolve({ ok: false, reason: 'not_configured' });
    }
    var selectedPoint = validPoint(options.coordinates);
    if (selectedPoint && !pointEquals(readPoint(), selectedPoint)) storePoint(selectedPoint);
    setPreparing(true);
    renderState('pending', false, false);
    return fetchCart().then(function (cart) {
      if (!isCurrent(ownGeneration)) return { ok: false, reason: 'stale' };
      if (!cart) throw new Error('cart_unavailable');
      if (cart.items.length === 0) {
        renderState('empty', true, false);
        return { ok: false, reason: 'empty_cart' };
      }
      if (cart.items.some(function (item) { return item.quantity > MAX_QUANTITY; })) {
        renderState('unavailable', true, false);
        return { ok: false, reason: 'cart_invalid' };
      }
      return clearStoredContext(ownGeneration).then(function (cleared) {
        if (!isCurrent(ownGeneration)) return { ok: false, reason: 'stale' };
        if (!cleared) throw new Error('context_clear_failed');
        clearInvalidationMarker();
        return acquireLocation(options.coordinates);
      }).then(function (point) {
        if (!isCurrent(ownGeneration)) return { ok: false, reason: 'stale' };
        var payload = { cart_id: cart.token, latitude: point.latitude, longitude: point.longitude, items: cart.items };
        return root.fetch(endpoint, {
          method: 'POST', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(function (response) {
          if (!isCurrent(ownGeneration)) return { stale: true };
          if (!response || response.status < 200 || response.status >= 300) throw new Error('context_unavailable');
          return response.json();
        }).then(function (body) {
          if (body && body.stale === true) return { ok: false, reason: 'stale' };
          if (!isCurrent(ownGeneration)) return { ok: false, reason: 'stale' };
          var expiresAt = body && parseExpiry(body.expires_at);
          if (!body || body.status !== 'ok' || typeof body.context !== 'string' || !body.context || expiresAt <= Date.now()) {
            var failure = stateFromFailure(body);
            renderState(failure, true, false);
            return { ok: false, reason: normalizedFailureReason(body, failure) };
          }
          var attributes = {};
          attributes[ATTR_CONTEXT] = body.context;
          attributes[ATTR_CART] = cart.token;
          attributes[ATTR_FINGERPRINT] = cart.fingerprint;
          attributes[ATTR_EXPIRES] = body.expires_at;
          return saveStoredContext(attributes, ownGeneration).then(function (saved) {
            if (!isCurrent(ownGeneration) || saved.stale) return { ok: false, reason: 'stale' };
            var after = saved.after;
            clearInvalidationMarker();
            renderState('ready', false, after.items.length > 0);
            scheduleExpiry(expiresAt);
            return {
              ok: true,
              context: body.context,
              expiresAt: body.expires_at,
              currency: typeof body.currency === 'string' ? body.currency : '',
              deliveryFeeMinor: typeof body.delivery_fee_minor === 'string' ? body.delivery_fee_minor : '',
              cart: after
            };
          });
        });
      });
    }).catch(function (error) {
      if (!isCurrent(ownGeneration)) return { ok: false, reason: 'stale' };
      markInvalidated();
      var kind = error && error.message === 'location_required' ? 'location' : 'unavailable';
      renderState(kind, true, false);
      return { ok: false, reason: error && error.message || 'context_unavailable' };
    }).finally(function () {
      if (isCurrent(ownGeneration)) setPreparing(false);
    });
  }

  function refreshAndMaybeReprepare(allowRefresh) {
    var ownGeneration = ++generation;
    if (allowRefresh) {
      markInvalidated();
      setPreparing(false);
      renderState('pending', false, false);
    }
    return fetchCart().then(function (cart) {
      if (!isCurrent(ownGeneration)) return;
      if (!cart || cart.items.length === 0) {
        clearTimeout(expiryTimer);
        renderState('empty', false, false);
        if (cart) return clearStoredContext(ownGeneration).then(function (cleared) {
          if (!isCurrent(ownGeneration)) return;
          if (cleared) clearInvalidationMarker();
        });
        return;
      }
      if (hasInvalidationMarker()) {
        renderState('pending', false, false);
        return clearStoredContext(ownGeneration).then(function (cleared) {
          if (!isCurrent(ownGeneration)) return;
          if (!cleared) {
            markInvalidated();
            renderState('pending', false, false);
            return;
          }
          clearInvalidationMarker();
          var point = readPoint();
          if (allowRefresh && point) return prepare({ coordinates: point });
          renderState('pending', false, false);
        });
      }
      if (hasStoredContext(cart)) {
        renderState('ready', false, true);
        scheduleExpiry(parseExpiry(cart.attributes[ATTR_EXPIRES]));
        return;
      }
      renderState('pending', false, false);
      return clearStoredContext(ownGeneration).then(function (cleared) {
        if (!isCurrent(ownGeneration)) return;
        if (!cleared) {
          markInvalidated();
          renderState('pending', false, false);
          return;
        }
        var point = readPoint();
        if (allowRefresh && point) return prepare({ coordinates: point });
        renderState('pending', false, false);
      });
    }).catch(function () {
      if (!isCurrent(ownGeneration)) return;
      markInvalidated();
      renderState('unavailable', true, false);
    });
  }

  function locationChanged(point) {
    var normalized = validPoint(point);
    var ownGeneration = ++generation;
    markInvalidated();
    storePoint(normalized);
    setPreparing(false);
    renderState('pending', false, false);
    return fetchCart().then(function (cart) {
      if (!isCurrent(ownGeneration) || !cart) return false;
      return clearStoredContext(ownGeneration).then(function (cleared) {
        if (!isCurrent(ownGeneration)) return false;
        if (cleared) clearInvalidationMarker();
        renderState(cart.items.length ? 'pending' : 'empty', false, false);
        return cleared;
      });
    }).catch(function () {
      // The session marker keeps any old cart token from re-enabling checkout
      // after reload until the stale attributes can be cleared successfully.
      return false;
    });
  }

  function bind() {
    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest('[data-samaanbox-context-prepare]');
      if (!button) return;
      event.preventDefault();
      prepare({ container: button.closest('[data-samaanbox-cart-context]') });
    });
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.querySelector('[name="checkout"]')) return;
      var button = form.querySelector('[name="checkout"]');
      if (button && button.disabled) {
        event.preventDefault();
        var status = form.querySelector('[data-samaanbox-context-status]') || document.querySelector('[data-samaanbox-context-status]');
        if (status) status.focus();
      }
    }, true);
    if (typeof root.subscribe === 'function') {
      try { root.subscribe('cart-update', function () { refreshAndMaybeReprepare(true); }); } catch (error) { /* event hooks are optional */ }
    }
    document.addEventListener('shopify:cart:change', function () { refreshAndMaybeReprepare(true); });
    document.addEventListener('cart:lines-update', function () { refreshAndMaybeReprepare(true); });
    refreshAndMaybeReprepare(false);
  }

  root.SamaanBoxCartContext = {
    prepare: prepare,
    rememberPoint: rememberPoint,
    readPoint: readPoint,
    locationChanged: locationChanged,
    normalizeCart: normalizeCart,
    hasStoredContext: hasStoredContext,
    storageKey: LOCATION_KEY
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();
})(window);
